import React from "react"
import PropTypes from "prop-types"
import { Button, Space, Flex } from "antd"
import { MinusOutlined, PlusOutlined } from "@ant-design/icons"
import AddOnPhoto from "ui/AddOnPhoto"
import { Text } from "ui/Typography"
import { useTranslation } from "react-i18next"
import { numberToCurrency } from "helpers/string"
import styles from "./AddOnsSelector.module.css"

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n))

const AddOnsSelector = ({ addOns, selections, onChange, disabled, headerExtra }) => {
  const { t } = useTranslation()
  const available = (addOns || []).filter((a) => a && a.enabled !== false)
  if (!available.length) return null

  const setQty = (id, qty) => {
    const next = { ...selections }
    if (qty <= 0) delete next[id]
    else next[id] = qty
    onChange({ target: { name: "add_ons", value: next } })
  }

  return (
    <div className={styles.wrapper}>
      <Flex justify="space-between" align="center" className={styles.header}>
        <Text strong className={styles.headerTitle}>
          {t("booking.add_ons.title")}
        </Text>
        {headerExtra}
      </Flex>
      <Space direction="vertical" className={styles.list}>
        {available.map((a) => {
          const min = a.min_per_order ?? 0
          const max = a.max_per_order ?? Infinity
          const qty = Number(selections?.[a.id] ?? 0)
          const startQty = Math.max(1, min)
          const onAdd = () => setQty(a.id, clamp(startQty, 0, max))
          const onInc = () => setQty(a.id, clamp(qty + 1, 0, max))
          const onDec = () => setQty(a.id, qty - 1 < min ? 0 : qty - 1)

          return (
            <div key={a.id} className={styles.row}>
              <Space>
                <AddOnPhoto src={a.photo_url} alt={a.title} size={48} iconSize={20} />
                <div>
                  <Text strong className={styles.itemTitle}>{a.title}</Text>
                  <Text type="secondary">{numberToCurrency(a.price)}</Text>
                </div>
              </Space>
              {qty === 0 ? (
                <Button variant="outlined" color="primary" onClick={onAdd} disabled={disabled}>
                  {t("experiences.add_ons.add")}
                </Button>
              ) : (
                <Space>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    icon={<MinusOutlined />}
                    onClick={onDec}
                    disabled={disabled}
                  />
                  <Text strong className={styles.qtyText}>{qty}</Text>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={onInc}
                    disabled={disabled || qty >= max}
                  />
                </Space>
              )}
            </div>
          )
        })}
      </Space>
    </div>
  )
}

AddOnsSelector.propTypes = {
  addOns: PropTypes.array,
  selections: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  headerExtra: PropTypes.node
}

AddOnsSelector.defaultProps = {
  addOns: [],
  selections: {},
  disabled: false
}

export default AddOnsSelector
